import { BASE_URL } from '../../../configs'
import {
  alertError,
  alertSuccess,
  createJsonHeaders,
  logoutOnRequestOrResponseBlob,
  logoutOnRequestOrResponseJson,
} from '../../../api'

/**
 * Care plan audit — patient-scoped generate / history / PDF / status.
 *
 * Endpoints (BASE_URL already includes `/private`):
 *   POST   /patients/{botId}/{patientId}/care-plan-audit
 *   GET    /patients/{botId}/{patientId}/care-plan-audit[?version=]
 *   GET    /patients/{botId}/{patientId}/care-plan-audit/{version}/pdf
 *   GET    /patients/{botId}/{patientId}/care-plan-audit/status
 *
 * Always hits the real API. Usage-meter stubbing (`isUsageStubEnabled`) is
 * intentionally not used here — QCS DEV tooling for Mark-as-QCS / usage meters
 * stays independent.
 *
 * Generate may return the full payload immediately. `getCarePlanAuditStatus`
 * reports whether a report is still generating server-side (`report_generating`).
 */

/** UI progress labels used by the table button and AuditResult overlay. */
export type AuditStatus = 'PENDING' | 'PROCESSING' | 'READY' | 'FAILED'

export type CarePlanAuditVersion = {
  version: number
  createdAt: string
  latest: boolean
}

/** One CQC standard block inside `current.standards` (field names may vary). */
export type CarePlanAuditStandard = {
  title?: string
  name?: string
  standard?: string
  label?: string
  strengths?: unknown
  strength?: unknown
  recommendations?: unknown
  recommendationsForImprovement?: unknown
  improvements?: unknown
  [key: string]: unknown
}

export type CarePlanAuditCurrent = {
  version: number
  totalVersions: number
  createdAt?: string
  standards?: CarePlanAuditStandard[]
  inconsistencies?: unknown
  cqcAlignment?: number
  alignment?: number
  summary?: string
  reviewer?: string
  [key: string]: unknown
}

export type CarePlanAuditPatient = {
  id: number
  firstName?: string
  lastName?: string
  botId?: number
  carePlans?: Array<{ originalFilename?: string; [key: string]: unknown }>
  [key: string]: unknown
}

export type CarePlanAuditPayload = {
  patient: CarePlanAuditPatient
  versions: CarePlanAuditVersion[]
  /** Null when the API returns no report body. */
  current: CarePlanAuditCurrent | null
}

const auditUrl = (botId: string | number, patientId: string | number, suffix = '') =>
  `${BASE_URL}/patients/${botId}/${patientId}/care-plan-audit${suffix}`

const unwrapData = (json: any): CarePlanAuditPayload | null => {
  const data = json?.data ?? json
  if (!data) return null
  const patient = data.patient || { id: 0 }
  const versions = Array.isArray(data.versions) ? data.versions : []
  if (!data.current) return null
  return {
    patient,
    versions,
    current: data.current,
  }
}

/** Newest-first sort so the sidebar matches prior UI expectations. */
const sortVersionsNewestFirst = (versions: CarePlanAuditVersion[]): CarePlanAuditVersion[] =>
  [...versions].sort((a, b) => Number(b.version) - Number(a.version))

/**
 * API often returns `current` with a version while `versions` is empty (first
 * audit). Backfill so Reviews shows Version N even for a single entry.
 */
const withCurrentInVersions = (payload: CarePlanAuditPayload): CarePlanAuditPayload => {
  const versions = Array.isArray(payload.versions) ? [...payload.versions] : []
  const cur = payload.current
  if (cur?.version == null) {
    return { ...payload, versions: sortVersionsNewestFirst(versions) }
  }
  if (!versions.some(v => Number(v.version) === Number(cur.version))) {
    // Only the backfilled current is latest — clear any stale latest flags.
    const cleared = versions.map(v => ({ ...v, latest: false }))
    cleared.push({
      version: cur.version,
      createdAt: cur.createdAt || '',
      latest: true,
    })
    return { ...payload, versions: sortVersionsNewestFirst(cleared) }
  }
  return { ...payload, versions: sortVersionsNewestFirst(versions) }
}

/**
 * Latest (or a specific) care plan audit for one service user.
 * Omitting `version` returns the latest/final version per the API.
 */
export const getCarePlanAudit = async (
  botId: string | number,
  patientId: number,
  version?: number,
  signal?: AbortSignal,
): Promise<CarePlanAuditPayload | null> => {
  try {
    const query = version != null ? `?version=${version}` : ''
    const response = await fetch(auditUrl(botId, patientId, query), {
      credentials: 'include',
      headers: createJsonHeaders(),
      method: 'GET',
      signal,
    })
    if (!response.ok) {
      // 404 = no audit yet. Other errors go through the logout helper so
      // 401/403 still expire the session the same way as the rest of the app.
      if (response.status === 404) return null
      try {
        await logoutOnRequestOrResponseJson(response)
      } catch {
        return null
      }
      return null
    }

    const json = await logoutOnRequestOrResponseJson(response)
    const payload = unwrapData(json)
    if (!payload) return null
    return withCurrentInVersions(payload)
  } catch (error) {
    if ((error as { name?: string })?.name === 'AbortError') throw error
    return null
  }
}

export type CarePlanAuditStatusDetails = {
  reportGenerating: boolean
  /** An audit was already generated for the currently uploaded care plan(s). */
  alreadyViewed: boolean
}

const IDLE_AUDIT_STATUS: CarePlanAuditStatusDetails = { reportGenerating: false, alreadyViewed: false }

/**
 * Server-side audit flags for this patient. Failures resolve idle so they never block Run.
 * GET /patients/{botId}/{patientId}/care-plan-audit/status → { report_generating, already_viewed }
 */
export const getCarePlanAuditStatusDetails = async (
  botId: string | number,
  patientId: number,
  signal?: AbortSignal,
): Promise<CarePlanAuditStatusDetails> => {
  try {
    const response = await fetch(auditUrl(botId, patientId, '/status'), {
      credentials: 'include',
      headers: createJsonHeaders(),
      method: 'GET',
      signal,
    })
    if (!response.ok) {
      if (response.status === 404) return IDLE_AUDIT_STATUS
      try {
        await logoutOnRequestOrResponseJson(response)
      } catch {
        return IDLE_AUDIT_STATUS
      }
      return IDLE_AUDIT_STATUS
    }
    const json = await logoutOnRequestOrResponseJson(response)
    const data = json?.data ?? json
    return {
      reportGenerating: Boolean(data?.report_generating),
      alreadyViewed: data?.already_viewed === true,
    }
  } catch (error) {
    if ((error as { name?: string })?.name === 'AbortError') throw error
    return IDLE_AUDIT_STATUS
  }
}

/**
 * Whether a care plan audit report is currently being generated for this patient.
 * GET /patients/{botId}/{patientId}/care-plan-audit/status → { report_generating }
 */
export const getCarePlanAuditStatus = async (
  botId: string | number,
  patientId: number,
  signal?: AbortSignal,
): Promise<boolean> => (await getCarePlanAuditStatusDetails(botId, patientId, signal)).reportGenerating

/**
 * Generates a care plan audit for one service user.
 *
 * Empty POST body per contract. Success returns the full payload (no job id).
 * Exhausted package allowance: HTTP 400 with error.code ERR_PACKAGE_USAGE_001
 * (do not treat every 400 as quota). Legacy 402 / 429 still mapped to quota.
 */
export const generateCarePlanAudit = async (
  botId: string | number,
  patientId: number,
  // Reserved for callers that still pass patient/org context (e.g. usage UX);
  // the real POST body is empty per API contract.
  context?: { patientName?: string; carePlans?: string[]; organisation?: string },
): Promise<{
  ok: boolean
  quotaExceeded?: boolean
  message?: string
  data?: CarePlanAuditPayload
}> => {
  void context
  try {
    const response = await fetch(auditUrl(botId, patientId), {
      credentials: 'include',
      headers: createJsonHeaders(),
      method: 'POST',
      body: JSON.stringify({}),
    })

    if (response.status === 402 || response.status === 429) {
      return { ok: false, quotaExceeded: true }
    }

    if (!response.ok) {
      let errorPayload: { code?: string; message?: string } | null = null
      try {
        const json = await response.json()
        errorPayload = json?.error || null
      } catch {
        errorPayload = null
      }

      if (errorPayload?.code === 'ERR_PACKAGE_USAGE_001') {
        return {
          ok: false,
          quotaExceeded: true,
          message: errorPayload.message || undefined,
        }
      }

      alertError(errorPayload?.message || 'Could not start the care plan audit. Please try again.')
      return { ok: false, message: errorPayload?.message }
    }

    const json = await logoutOnRequestOrResponseJson(response)
    const data = unwrapData(json)
    // POST must return a report body.
    if (!data?.current) {
      alertError('Could not start the care plan audit. Please try again.')
      return { ok: false }
    }

    alertSuccess('Audit ready.', 3000)
    return {
      ok: true,
      data: withCurrentInVersions(data),
    }
  } catch {
    alertError('Could not start the care plan audit. Please try again.')
    return { ok: false }
  }
}

const saveBlob = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

/** Downloads a finished audit PDF for a given version number. */
export const downloadCarePlanAudit = async (
  botId: string | number,
  patientId: number,
  version: number,
  filename = 'care-plan-audit.pdf',
): Promise<void> => {
  try {
    const response = await fetch(auditUrl(botId, patientId, `/${version}/pdf`), {
      credentials: 'include',
      method: 'GET',
    })
    const blob = await logoutOnRequestOrResponseBlob(response)
    saveBlob(blob, filename)
  } catch {
    alertError('Could not download the audit PDF.')
  }
}
